'use client';

/* ──────────────────────────────────────────────
 *  Confirmation enrichie (spec Parcours P3 § 7.7, planches 3 et 8) :
 *  titre selon le statut, récap complet, « Ajouter au calendrier »,
 *  gestion du rendez-vous, « Prendre un autre rendez-vous » et, sur un
 *  site vitrine, « Retour au site ».
 * ────────────────────────────────────────────── */

import { type JSX, useEffect, useState } from 'react';
import { ReservationStatus, public_env, type BookingConfirmationResponseObject } from '@cfixe-monorepo/shared';
import { useBooking } from '../../context/BookingContext';
import { BookingManageView } from '../manage/BookingManageView';
import { BookingOutcome } from '../shared/BookingOutcome';
import { BookingConfirmationRecap } from '../shared/BookingConfirmationRecap';
import { bookingCalendarIcsUrl, getBookingConfirmation } from '../../../website/api/professional-api';

/**
 * Page de gestion hébergée par Propelsia (`/book/<slug>/manage/<uuid>`, celle du lien de l'email), en absolu :
 * le tunnel tourne aussi sur le domaine d'un site tiers (widget embarqué), où un chemin relatif mène à une 404.
 * Sans origine connue, aucun lien : l'email de confirmation le porte.
 */
function hostedManageUrl(professionalSlug: string, reservationUuid: string): string | undefined {
  const origin = public_env.APP_BASE_URL.replace(/\/$/, '');
  if (!origin) return undefined;
  return `${origin}/book/${encodeURIComponent(professionalSlug)}/manage/${encodeURIComponent(reservationUuid)}`;
}

export function BookingSuccess(): JSX.Element {
  const { ctx, labels, professionalSlug, successMode, backToSiteUrl, restartTunnel } = useBooking();
  const reservationUuid = ctx.confirmedReservationUuid ?? ctx.preReservationUuid;
  // Mode « message » : lien vers la page de gestion hébergée (en `manage-inline`, la gestion s'ouvre ici).
  const manageHref =
    professionalSlug && reservationUuid ? hostedManageUrl(professionalSlug, reservationUuid) : undefined;
  const canManageInline = successMode === 'manage-inline' && Boolean(reservationUuid);
  const [manageOpen, setManageOpen] = useState(false);

  // Statut réel de la réservation : un pro qui « valide chaque réservation » (confirm_every_booking)
  // laisse la résa en WAITING_CONFIRMATION → on annonce l'attente de validation au lieu d'une
  // confirmation ferme. `undefined` tant que le statut n'est pas connu → AUCUN titre, pas de flash « confirmée ».
  const [booking, setBooking] = useState<BookingConfirmationResponseObject | null | undefined>(undefined);
  useEffect(() => {
    // `return undefined` (et non `return;`) pour satisfaire noImplicitReturns du build lib :
    // l'autre branche renvoie une fonction de cleanup, donc tous les chemins doivent renvoyer.
    if (!reservationUuid) return undefined;
    let cancelled = false;
    getBookingConfirmation(reservationUuid, { next: { revalidate: 0 } })
      .then((data) => {
        if (!cancelled) setBooking(data);
      })
      .catch(() => {
        // À défaut de récap, le message de confirmation seul (comportement historique).
        if (!cancelled) setBooking(null);
      });
    return () => {
      cancelled = true;
    };
  }, [reservationUuid]);

  const statusKnown = booking !== undefined;
  const isAwaitingConfirmation = booking?.reservation_status === ReservationStatus.WAITING_CONFIRMATION;
  // Annulée depuis la gestion intégrée (ou par le pro entre-temps) : l'état annulé remplace « confirmée »,
  // sans récap ni « Ajouter au calendrier » d'un rendez-vous qui n'a plus lieu.
  const isCancelled =
    booking?.reservation_status === ReservationStatus.CANCELLED_BY_CUSTOMER ||
    booking?.reservation_status === ReservationStatus.CANCELLED_BY_PRO;
  const title = !statusKnown
    ? undefined
    : isCancelled
      ? labels.booking_cancelled
      : isAwaitingConfirmation
        ? labels.confirmation_pending_title
        : labels.booking_confirmed;
  const message = isAwaitingConfirmation ? labels.booking_awaiting_confirmation_message : undefined;

  const actions = statusKnown ? (
    <div className="bt-confirmation-actions">
      {canManageInline ? (
        <button
          type="button"
          className="bt-cta bt-cta--secondary"
          aria-expanded={manageOpen}
          onClick={(): void => setManageOpen((open): boolean => !open)}
        >
          {labels.manage_booking}
        </button>
      ) : manageHref ? (
        <a className="bt-cta bt-cta--secondary bt-confirmed__manage-link" href={manageHref}>
          {labels.manage_booking}
        </a>
      ) : null}
      <button type="button" className="bt-cta bt-cta--secondary" onClick={restartTunnel}>
        {labels.book_another}
      </button>
      {backToSiteUrl ? (
        <a className="bt-link-subtle" href={backToSiteUrl}>
          {labels.back_to_site}
        </a>
      ) : null}
    </div>
  ) : null;

  return (
    <BookingOutcome
      awaitingConfirmation={isAwaitingConfirmation}
      title={title}
      message={message}
      withManage={manageOpen}
    >
      {booking && reservationUuid && !isCancelled ? (
        <BookingConfirmationRecap booking={booking} labels={labels} icsUrl={bookingCalendarIcsUrl(reservationUuid)} />
      ) : null}
      {actions}
      {manageOpen && reservationUuid ? (
        /* Report ou annulation faits ici : la réservation rechargée par la gestion remplace celle du récap
           (nouvelle date, nouveau lien d'agenda) — jamais de récap périmé. */
        <BookingManageView
          uuid={reservationUuid}
          className="booking-manage-view--inline"
          onRescheduleSuccess={setBooking}
          onCancelSuccess={setBooking}
        />
      ) : null}
    </BookingOutcome>
  );
}
